import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { propTypes as formPropTypes, formValueSelector, Form } from 'redux-form'
import { selectors as globalSchoolSelectors } from 'school-portal-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import * as Types from 'types'
import { InfoModal, ExpandableItem } from 'components'
import {
  PartnerProgramTypeForm,
  RequestedDisciplinesForm,
  ProgramsResourcesInterestForm,
  PartnerOutcomeTypeForm,
  LanguageForm,
} from '../forms'
import { ArtsProgrammingResourceForm } from '../../survey/forms'
import * as apiActions from 'api-actions'
import {
  communityEnumerable,
  getSpecificInterestTypeId,
  removeSpecificInterestType,
  replaceResources,
  useDynamicInfoModal,
  useCommunity,
  getOtherTypeId,
  persistSubmitSucceeded,
} from 'utils'
import { get, isEmpty } from 'lodash'
import { SubmitButton } from 'lp-components'
import { lpForm } from 'lp-form'

const propTypes = {
  ...formPropTypes,
  schoolId: PropTypes.number.isRequired,
  school: Types.school.isRequired,
  updateSchool: PropTypes.func.isRequired,
  resourceTypes: PropTypes.array.isRequired,
  programTypes: PropTypes.array.isRequired,
  languages: PropTypes.array.isRequired,
  outcomeTypes: PropTypes.array.isRequired,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  detail: Types.detail.isRequired,
  fetchCompletionSchool: PropTypes.func,
  profileCompletionSchoolYear: Types.schoolYear,
  currentArtsResources: PropTypes.array,
  hideSubmit: PropTypes.bool,
}

const defaultProps = {
  currentArtsResources: [],
  hideSubmit: false,
}

function Interests({
  school,
  updateSchool,
  resourceTypes,
  programTypes,
  languages,
  outcomeTypes,
  detail,
  disciplines,
  handleSubmit,
  submitSucceeded,
  pristine,
  submitting,
  expandForm,
  fetchCompletionSchool,
  profileCompletionSchoolYear,
  change,
  currentArtsResources,
  hideSubmit,
}) {
  const community = useCommunity()
  const currentSchoolYearId = detail && detail.schoolYearId
  const [showInfoModal, infoModalContent, setShowInfoModalWithContent] =
    useDynamicInfoModal()
  const specificInterestTypeId = useMemo(
    () => getSpecificInterestTypeId(resourceTypes),
    [resourceTypes]
  )
  const showInterestsQuestion = !!specificInterestTypeId
  const updateSchoolOnSubmit = useCallback(
    (params) => updateSchool(school.id, currentSchoolYearId, params),
    [school, currentSchoolYearId]
  )
  const outcomesModalContent = useMemo(() => {
    return communityEnumerable.getInfoModalContent(outcomeTypes)
  }, [outcomeTypes])

  const onSubmitAll = async (values) => {
    const params = { type: Types.REQUEST_FORM_TYPE }

    // Arts resources
    const artsRequested = get(values, 'arts.requestedResources', [])
    const artsOtherText = get(values, 'arts.otherText', '')
    if (artsRequested || artsOtherText) {
      const artsResources = replaceResources({
        old: school.requestedResources,
        new: artsRequested,
      })
      if (artsOtherText) {
        const otherResourceTypeId = getOtherTypeId(resourceTypes)
        artsResources.push({
          resourceTypeId: otherResourceTypeId,
          otherText: artsOtherText,
        })
      }
      params.resources = artsResources
    }

    // Specific interest
    const otherText = get(values, 'interests.otherText', '')
    if (showInterestsQuestion) {
      const specificResources = replaceResources({
        old: [school.requestedSpecificInterest],
        new: [
          {
            resourceTypeId: specificInterestTypeId,
            otherText,
          },
        ],
      })
      params.resources = (params.resources || []).concat(specificResources)
    }

    // Program types
    const requestedProgramTypes = get(
      values,
      'programTypes.requestedProgramTypes',
      []
    )
    const programTypeOtherText = get(values, 'programTypes.otherText', '')
    if (requestedProgramTypes || programTypeOtherText) {
      const programTypesResources = replaceResources({
        old: school.requestedProgramTypes,
        new: requestedProgramTypes,
      })
      if (programTypeOtherText) {
        const otherTypeId = getOtherTypeId(programTypes)
        programTypesResources.push({
          programTypeId: otherTypeId,
          otherText: programTypeOtherText,
        })
      }
      if (programTypesResources.length)
        params.programTypes = programTypesResources
    }

    // Languages
    const requestedLanguages = get(values, 'languages.requestedLanguages', [])
    const languageOtherText = get(values, 'languages.otherText', '')
    if (requestedLanguages || languageOtherText) {
      const languagesResources = replaceResources({
        old: school.requestedLanguages,
        new: requestedLanguages,
      })
      if (languageOtherText) {
        const otherLanguageId = getOtherTypeId(languages)
        languagesResources.push({
          languageId: otherLanguageId,
          otherText: languageOtherText,
        })
      }
      if (languagesResources.length) params.languages = languagesResources
    }

    // Outcome types
    const requestedOutcomeTypes = get(
      values,
      'outcomes.requestedOutcomeTypes',
      []
    )
    const outcomeOtherText = get(values, 'outcomes.otherText', '')
    if (requestedOutcomeTypes || outcomeOtherText) {
      const outcomesResources = replaceResources({
        old: school.requestedOutcomeTypes,
        new: requestedOutcomeTypes,
      })
      if (outcomeOtherText) {
        const otherOutcomeTypeId = getOtherTypeId(outcomeTypes)
        outcomesResources.push({
          outcomeTypeId: otherOutcomeTypeId,
          otherText: outcomeOtherText,
        })
      }
      if (outcomesResources.length) params.outcomes = outcomesResources
    }

    // Disciplines
    const subDisciplines = get(values, 'disciplines.subDisciplines', [])
    const subDisciplineResources = replaceResources({
      old: school.requestedPrograms,
      new: subDisciplines,
    })
    if (subDisciplineResources.length)
      params.subDisciplines = subDisciplineResources

    // Let redux-form observe success/failure so a page-level Save button can
    // orchestrate this form alongside others. Errors propagate to onSubmitFail.
    const updatedSchool = await updateSchoolOnSubmit(params)
    // Refresh the current-school-year completion school so the progress bar
    // reflects this save immediately (updateSchool only updates the global,
    // survey-year school).
    if (fetchCompletionSchool && profileCompletionSchoolYear) {
      fetchCompletionSchool(school.id, profileCompletionSchoolYear.id)
    }
    return updatedSchool
  }

  return (
    <div className="expandable-section interest-expander card">
      <ExpandableItem
        headerTitle={'Interests'}
        startExpanded={expandForm}
        expandTriggerable={true}
      >
        <div className="expander-content">
          <Form onSubmit={handleSubmit(onSubmitAll)}>
            <section className="card-section">
              <h2>
                What arts-programming resource(s) would you be interested in
                receiving for your school? Check all that apply.
              </h2>
              <ArtsProgrammingResourceForm
                resourceTypes={removeSpecificInterestType(resourceTypes)}
                prefix="arts"
                change={change}
                requestedResources={currentArtsResources}
              />
            </section>
            <section className="card-section">
              <h2>
                {get(
                  community,
                  'displayText.schoolPortal.summary.partnerProgramTypes.header',
                  'What types of partner programs would you be interested in having at your school? Check all that apply.'
                )}
              </h2>
              <PartnerProgramTypeForm
                programTypes={programTypes}
                prefix="programTypes"
              />
            </section>
            {languages.length > 0 && (
              <section className="card-section">
                <h2>
                  In which languages would you be interested in having programs
                  offered?
                </h2>
                <LanguageForm languages={languages} prefix="languages" />
              </section>
            )}

            {!isEmpty(outcomeTypes) && (
              <section className="card-section">
                <h2>
                  Indicate the{' '}
                  <button
                    className="link-secondary interests-link"
                    type="button"
                    onClick={() =>
                      setShowInfoModalWithContent(outcomesModalContent)
                    }
                  >
                    qualities and desired outcomes
                  </button>{' '}
                  you seek in a partner. Check all that apply.
                </h2>
                <PartnerOutcomeTypeForm
                  outcomeTypes={outcomeTypes}
                  prefix="outcomes"
                />
              </section>
            )}
            <section className="card-section">
              <h2>
                An arts and cultural partnership can help enhance your
                curriculum and augment your arts program. What, if any, types of
                discipline area(s) would you be interested in pursuing with an
                external arts program provider (i.e., arts organization)?
              </h2>
              <RequestedDisciplinesForm
                disciplines={disciplines}
                prefix="disciplines"
              />
            </section>
            {showInterestsQuestion && (
              <section className="card-section">
                <h2>School's Interests</h2>
                <ProgramsResourcesInterestForm prefix="interests" />
              </section>
            )}
            {!hideSubmit && (
              <div className="button-wrapper">
                <SubmitButton
                  {...{ pristine: submitSucceeded && pristine, submitting }}
                >
                  Save Responses
                </SubmitButton>
              </div>
            )}
            {showInfoModal && (
              <InfoModal
                onClose={() => setShowInfoModalWithContent(false)}
                content={infoModalContent.content}
                header={infoModalContent.header}
              />
            )}
          </Form>
        </div>
      </ExpandableItem>
    </div>
  )
}

Interests.propTypes = propTypes
Interests.defaultProps = defaultProps

const selectInterestsValue = formValueSelector('school-interests')

function mapStateToProps(state) {
  // The Interests section is a current-school-year concept: the completion bar
  // and the server's completion check both evaluate the current year. The global
  // `school` gets (re)loaded for the survey year, so read and save interests
  // against `completionSchool` (the current-year copy) to avoid writing them to
  // the wrong year. Fall back to the global school while completionSchool loads.
  const school =
    globalSchoolSelectors.completionSchool(state) ||
    globalSchoolSelectors.school(state)
  return {
    schoolId: globalSchoolSelectors.currentSchoolId(state),
    detail: school && school.detail,
    school,
    resourceTypes: globalSelectors.resourceTypes(state),
    programTypes: globalSelectors.orderedProgramTypes(state),
    languages: globalSelectors.languages(state),
    disciplines: globalSelectors.definedDisciplines(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    profileCompletionSchoolYear: globalSelectors.currentSchoolYear(state),
    currentArtsResources: selectInterestsValue(
      state,
      'arts.requestedResources'
    ),
    initialValues: (() => {
      const otherProgramTypeId = getOtherTypeId(
        globalSelectors.orderedProgramTypes(state)
      )
      const otherOutcomeTypeId = getOtherTypeId(
        globalSelectors.outcomeTypes(state)
      )
      const otherLanguageId = getOtherTypeId(globalSelectors.languages(state))
      const otherResourceTypeId = getOtherTypeId(
        globalSelectors.resourceTypes(state)
      )

      const school =
        globalSchoolSelectors.completionSchool(state) ||
        globalSchoolSelectors.school(state)
      const disciplines = globalSelectors.definedDisciplines(state)

      const resourceOther = (school.requestedResources || []).find(
        ({ resourceTypeId }) => resourceTypeId === otherResourceTypeId
      )
      const resourceRequested = (school.requestedResources || []).filter(
        ({ resourceTypeId }) => resourceTypeId !== otherResourceTypeId
      )

      const programOther = (school.requestedProgramTypes || []).find(
        ({ programTypeId }) => programTypeId === otherProgramTypeId
      )
      const programRequested = (school.requestedProgramTypes || []).filter(
        ({ programTypeId }) => programTypeId !== otherProgramTypeId
      )

      const languageOther = (school.requestedLanguages || []).find(
        ({ languageId }) => languageId === otherLanguageId
      )
      const languageRequested = (school.requestedLanguages || []).filter(
        ({ languageId }) => languageId !== otherLanguageId
      )

      const outcomeOther = (school.requestedOutcomeTypes || []).find(
        ({ outcomeTypeId }) => outcomeTypeId === otherOutcomeTypeId
      )
      const outcomeRequested = (school.requestedOutcomeTypes || []).filter(
        ({ outcomeTypeId }) => outcomeTypeId !== otherOutcomeTypeId
      )

      function disciplineForSubDiscipline(subDiscipline) {
        return disciplines.find(({ subDisciplines }) =>
          subDisciplines.some(({ id }) => id === subDiscipline.subDisciplineId)
        )
      }

      const enrichedSubDisciplines = (school.requestedPrograms || []).map(
        (sd) => ({
          ...sd,
          disciplineName: disciplineForSubDiscipline(sd).name,
        })
      )

      return {
        arts: {
          requestedResources: resourceRequested || [],
          otherText: resourceOther ? resourceOther.otherText : '',
        },
        programTypes: {
          requestedProgramTypes: programRequested || [],
          otherText: programOther ? programOther.otherText : '',
        },
        languages: {
          requestedLanguages: languageRequested || [],
          otherText: languageOther ? languageOther.otherText : '',
        },
        outcomes: {
          requestedOutcomeTypes: outcomeRequested || [],
          otherText: outcomeOther ? outcomeOther.otherText : '',
        },
        disciplines: {
          subDisciplines: enrichedSubDisciplines,
        },
        interests: {
          otherText: get(school, 'requestedSpecificInterest.otherText', ''),
        },
      }
    })(),
  }
}

const mapDispatchToProps = {
  updateSchool: apiActions.updateSchool,
  fetchCompletionSchool: apiActions.fetchCompletionSchool,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lpForm({
    name: 'school-interests',
    enableReinitialize: true,
    constraints: {
      'arts.otherText': {
        exclusion: {
          within: [' '],
          message: '^Description is required',
        },
      },
    },
  }),
  persistSubmitSucceeded()
)(Interests)
